import { useState, useEffect, useCallback, useRef } from 'react'
import {
  getAdminAnalyticsOverview,
  getAdminAnalyticsMerchants,
  getAdminAnalyticsSearchGaps,
  getAdminAnalyticsSources,
  getAdminAnalyticsRetention,
  getAdminAnalyticsChatContext,
  getUserGrowth,
  getTopUsers,
  type AnalyticsOverview,
  type MerchantLeaderboardEntry,
  type SearchGaps,
  type SourceBreakdown,
  type Retention,
  type ChatContext,
  type UserGrowthResponse,
  type TopUserRow,
} from '../../api/admin'
import { useNavigate } from 'react-router-dom'

// ── Helpers ────────────────────────────────────────────────────────────────

const card: React.CSSProperties = {
  background: 'var(--app-card-bg)',
  borderRadius: 16,
  padding: '16px 18px',
}

const sectionTitle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  opacity: 0.45,
  color: 'var(--tg-theme-text-color)',
  marginBottom: 12,
}

const text: React.CSSProperties = { color: 'var(--tg-theme-text-color)' }
const subtle: React.CSSProperties = { color: 'var(--tg-theme-text-color)', opacity: 0.5 }

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

function KpiCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ ...subtle, fontSize: 12 }}>{label}</div>
      <div style={{ ...text, fontSize: 24, fontWeight: 700, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ ...subtle, fontSize: 12 }}>{sub}</div>}
    </div>
  )
}

// ── Rich SVG line chart ────────────────────────────────────────────────────

function niceMax(raw: number): number {
  if (raw <= 0) return 1
  const pow = Math.pow(10, Math.floor(Math.log10(raw)))
  const n = raw / pow
  let nice: number
  if (n <= 1) nice = 1
  else if (n <= 2) nice = 2
  else if (n <= 2.5) nice = 2.5
  else if (n <= 5) nice = 5
  else nice = 10
  return nice * pow
}

function shortDate(iso?: string): string {
  if (!iso) return ''
  // accept "YYYY-MM-DD" or full ISO
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return iso
  return `${m[2]}/${m[3]}`
}

function LineChart({
  data,
  keys,
  colors,
  labels,
  height = 160,
}: {
  data: Record<string, number | string>[]
  keys: string[]
  colors: string[]
  labels?: string[]
  height?: number
}) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [w, setW] = useState(600)

  useEffect(() => {
    if (!wrapRef.current) return
    const el = wrapRef.current
    const update = () => setW(Math.max(280, Math.floor(el.getBoundingClientRect().width)))
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  if (!data.length) return null

  // SVG geometry — viewBox matches CSS px so text/font-size renders at the intended size.
  const h = height
  const padL = 8 // left padding (we'll put y labels on the right instead)
  const padR = 44 // right reserved for y-axis labels
  const padT = 16
  const padB = 22 // bottom reserved for date labels
  const innerW = w - padL - padR
  const innerH = h - padT - padB

  const allVals = data.flatMap((d) => keys.map((k) => Number(d[k] || 0)))
  const rawMax = Math.max(...allVals, 1)
  const yMax = niceMax(rawMax)
  const yMid = yMax / 2

  const xFor = (i: number) =>
    padL + (data.length === 1 ? innerW / 2 : (i / (data.length - 1)) * innerW)
  const yFor = (v: number) => padT + innerH - (v / yMax) * innerH

  const dates = data.map((d) => String(d.date ?? ''))

  // Build x-axis tick indices: first, middle, last (or up to 4 evenly spaced)
  const tickCount = Math.min(4, data.length)
  const tickIdxs: number[] =
    tickCount <= 1
      ? [0]
      : Array.from({ length: tickCount }, (_, i) =>
          Math.round((i / (tickCount - 1)) * (data.length - 1)),
        )

  // Summary per key
  const summary = keys.map((key, idx) => {
    const vals = data.map((d) => Number(d[key] || 0))
    const total = vals.reduce((a, b) => a + b, 0)
    const peakVal = Math.max(...vals)
    const peakIdx = vals.indexOf(peakVal)
    const avg = vals.length ? total / vals.length : 0
    return {
      key,
      label: labels?.[idx] ?? key,
      color: colors[idx],
      total,
      avg,
      peakVal,
      peakIdx,
    }
  })

  // Helper: path "d" for one series
  const pathFor = (key: string) =>
    data
      .map((d, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(1)} ${yFor(Number(d[key] || 0)).toFixed(1)}`)
      .join(' ')

  const areaFor = (key: string) => {
    if (data.length < 2) return ''
    const top = data
      .map((d, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(1)} ${yFor(Number(d[key] || 0)).toFixed(1)}`)
      .join(' ')
    const baseY = (padT + innerH).toFixed(1)
    return `${top} L ${xFor(data.length - 1).toFixed(1)} ${baseY} L ${xFor(0).toFixed(1)} ${baseY} Z`
  }

  const gridColor = 'currentColor'
  const yTicks = [0, yMid, yMax]

  return (
    <div ref={wrapRef}>
      <svg
        width="100%"
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        style={{ display: 'block', overflow: 'visible' }}
      >
        {/* Gridlines + y-axis labels */}
        {yTicks.map((v, i) => {
          const y = yFor(v)
          return (
            <g key={i} style={{ color: 'var(--tg-theme-text-color)' }}>
              <line
                x1={padL}
                x2={padL + innerW}
                y1={y}
                y2={y}
                stroke={gridColor}
                strokeWidth={1}
                opacity={i === 0 ? 0.15 : 0.08}
                strokeDasharray={i === 0 ? undefined : '3 3'}
                vectorEffect="non-scaling-stroke"
              />
              <text
                x={padL + innerW + 6}
                y={y}
                fontSize={11}
                fill="currentColor"
                opacity={0.5}
                dominantBaseline="middle"
                textAnchor="start"
              >
                {fmt(v)}
              </text>
            </g>
          )
        })}

        {/* x-axis date labels */}
        {tickIdxs.map((i) => (
          <text
            key={i}
            x={xFor(i)}
            y={padT + innerH + 14}
            fontSize={11}
            fill="currentColor"
            opacity={0.5}
            textAnchor={i === 0 ? 'start' : i === data.length - 1 ? 'end' : 'middle'}
            style={{ color: 'var(--tg-theme-text-color)' }}
          >
            {shortDate(dates[i])}
          </text>
        ))}

        {/* Area fills (only when single series, for clarity) */}
        {keys.length === 1 && (
          <path d={areaFor(keys[0])} fill={colors[0]} opacity={0.12} />
        )}

        {/* Series lines */}
        {keys.map((key, idx) => (
          <path
            key={key}
            d={pathFor(key)}
            fill="none"
            stroke={colors[idx]}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}

        {/* Last-value dots + values for each series */}
        {summary.map((s, idx) => {
          const lastI = data.length - 1
          const lastV = Number(data[lastI][s.key] || 0)
          return (
            <g key={`last-${idx}`}>
              <circle
                cx={xFor(lastI)}
                cy={yFor(lastV)}
                r={3.5}
                fill={s.color}
                stroke="var(--app-card-bg)"
                strokeWidth={2}
              />
            </g>
          )
        })}

        {/* Peak markers (only when single series — avoids clutter) */}
        {keys.length === 1 &&
          summary[0].peakVal > 0 && (
            <g>
              <circle
                cx={xFor(summary[0].peakIdx)}
                cy={yFor(summary[0].peakVal)}
                r={3.5}
                fill={summary[0].color}
                stroke="var(--app-card-bg)"
                strokeWidth={2}
              />
              <text
                x={xFor(summary[0].peakIdx)}
                y={yFor(summary[0].peakVal) - 8}
                fontSize={11}
                fontWeight={700}
                fill={summary[0].color}
                textAnchor="middle"
              >
                {fmt(summary[0].peakVal)}
              </text>
            </g>
          )}

        {/* Hover affordance: invisible vertical bands with <title> */}
        {data.map((d, i) => {
          const bandW = innerW / Math.max(data.length, 1)
          const cx = xFor(i)
          const title = keys
            .map((k, ki) => `${labels?.[ki] ?? k}: ${fmt(Number(d[k] || 0))}`)
            .join('\n')
          return (
            <rect
              key={`hit-${i}`}
              x={cx - bandW / 2}
              y={padT}
              width={bandW}
              height={innerH}
              fill="transparent"
            >
              <title>{`${shortDate(dates[i])}\n${title}`}</title>
            </rect>
          )
        })}
      </svg>

      {/* Summary strip */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${summary.length}, 1fr)`,
          gap: 8,
          marginTop: 10,
        }}
      >
        {summary.map((s) => (
          <div
            key={s.key}
            style={{
              background: 'var(--app-page-bg)',
              borderRadius: 10,
              padding: '8px 10px',
              borderLeft: `3px solid ${s.color}`,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'var(--tg-theme-text-color)',
                opacity: 0.55,
              }}
            >
              {s.label}
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: 'var(--tg-theme-text-color)',
                marginTop: 2,
                letterSpacing: '-0.01em',
              }}
            >
              {fmt(s.total)}
            </div>
            <div
              style={{
                fontSize: 10,
                color: 'var(--tg-theme-text-color)',
                opacity: 0.5,
                marginTop: 2,
              }}
            >
              avg {fmt(Math.round(s.avg))}/d · peak {fmt(s.peakVal)}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Bar chart ─────────────────────────────────────────────────────────────

function BarChart({
  items,
  color = 'var(--tg-theme-button-color)',
}: {
  items: { label: string; value: number }[]
  color?: string
}) {
  if (!items.length) return null
  const max = Math.max(...items.map((i) => i.value), 1)
  const totalAll = items.reduce((a, b) => a + b.value, 0)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map(({ label, value }) => {
        const pct = totalAll > 0 ? (value / totalAll) * 100 : 0
        return (
          <div key={label}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 4,
                fontSize: 13,
                ...text,
              }}
            >
              <span style={{ fontWeight: 500 }}>{label}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                <span style={{ fontWeight: 700 }}>{fmt(value)}</span>
                {totalAll > 0 && (
                  <span style={{ ...subtle, marginLeft: 6, fontSize: 11 }}>
                    {pct.toFixed(pct < 10 ? 1 : 0)}%
                  </span>
                )}
              </span>
            </div>
            <div
              style={{
                height: 8,
                borderRadius: 4,
                background: 'var(--app-page-bg)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${(value / max) * 100}%`,
                  borderRadius: 4,
                  background: color,
                  transition: 'width 0.4s ease',
                }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Days Picker ────────────────────────────────────────────────────────────

function DaysPicker({
  value,
  onChange,
  options = [7, 30, 90],
}: {
  value: number
  onChange: (d: number) => void
  options?: number[]
}) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {options.map((d) => (
        <button
          key={d}
          onClick={() => onChange(d)}
          style={{
            padding: '4px 12px',
            borderRadius: 10,
            border: 'none',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 600,
            background: value === d ? 'var(--tg-theme-button-color)' : 'var(--app-card-bg)',
            color: value === d ? 'var(--tg-theme-button-text-color)' : 'var(--tg-theme-text-color)',
          }}
        >
          {d}d
        </button>
      ))}
    </div>
  )
}

// ── Section skeleton ───────────────────────────────────────────────────────

function Skeleton() {
  return (
    <div
      style={{
        height: 80,
        borderRadius: 16,
        background: 'var(--app-card-bg)',
        opacity: 0.4,
        animation: 'pulse 1.5s ease-in-out infinite',
      }}
    />
  )
}

// ── Legend dot ────────────────────────────────────────────────────────────

function Dot({ color }: { color: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: color,
        marginRight: 4,
      }}
    />
  )
}

// ── Chat-type metadata + tooltip helpers ──────────────────────────────────

const CHAT_TYPE_DESCRIPTIONS: Record<string, string> = {
  private: 'Opened from a 1:1 chat with the bot',
  group: 'Opened from a basic group chat',
  supergroup: 'Opened from a supergroup',
  channel: 'Opened from a channel',
  sender: 'Opened via the attachment menu / contact-share flow',
  unknown: 'chat_type was not provided by Telegram (older clients or direct deep-link)',
}

const CHAT_TYPE_COLOR: Record<string, string> = {
  private: '#3b82f6',
  group: '#22c55e',
  supergroup: '#a855f7',
  channel: '#f59e0b',
  sender: '#06b6d4',
  unknown: '#6b7280',
}

function InfoTooltip({ text: t }: { text: string }) {
  return (
    <span
      title={t}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 14,
        height: 14,
        marginLeft: 6,
        fontSize: 10,
        fontWeight: 700,
        borderRadius: '50%',
        background: 'var(--app-page-bg)',
        color: 'var(--tg-theme-text-color)',
        opacity: 0.6,
        cursor: 'help',
        verticalAlign: 'middle',
      }}
    >
      ?
    </span>
  )
}

function ChatTypeBadge({ type }: { type: string }) {
  const color = CHAT_TYPE_COLOR[type] ?? '#6b7280'
  return (
    <span
      title={CHAT_TYPE_DESCRIPTIONS[type] ?? type}
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 999,
        background: `${color}22`,
        color,
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'capitalize',
        cursor: 'help',
      }}
    >
      {type}
    </span>
  )
}

function formatLastSeen(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const now = Date.now()
  const diffMs = now - d.getTime()
  const diffHr = diffMs / (1000 * 60 * 60)
  if (diffHr < 1) return 'just now'
  if (diffHr < 24) return `${Math.floor(diffHr)}h ago`
  const diffDays = Math.floor(diffHr / 24)
  if (diffDays < 30) return `${diffDays}d ago`
  return shortDate(iso)
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function AdminAnalyticsPage() {
  const [days, setDays] = useState(30)
  const [leaderboardDays, setLeaderboardDays] = useState(30)

  const [overview, setOverview] = useState<AnalyticsOverview | null>(null)
  const [merchants, setMerchants] = useState<MerchantLeaderboardEntry[]>([])
  const [gaps, setGaps] = useState<SearchGaps | null>(null)
  const [sources, setSources] = useState<SourceBreakdown | null>(null)
  const [retention, setRetention] = useState<Retention | null>(null)
  const [chatContext, setChatContext] = useState<ChatContext | null>(null)

  const [loadingOverview, setLoadingOverview] = useState(true)
  const [loadingMerchants, setLoadingMerchants] = useState(true)
  const [loadingGaps, setLoadingGaps] = useState(true)
  const [loadingSources, setLoadingSources] = useState(true)
  const [loadingRetention, setLoadingRetention] = useState(true)
  const [loadingChatContext, setLoadingChatContext] = useState(true)
  const [instanceFilter, setInstanceFilter] = useState<string | null>(null)

  const [growth, setGrowth] = useState<UserGrowthResponse | null>(null)
  const [topUsers, setTopUsers] = useState<TopUserRow[]>([])
  const [topMetric, setTopMetric] = useState<'views' | 'sessions' | 'likes'>('views')
  const navigate = useNavigate()

  const fetchOverview = useCallback(async () => {
    setLoadingOverview(true)
    try {
      setOverview(await getAdminAnalyticsOverview(days))
    } finally {
      setLoadingOverview(false)
    }
  }, [days])

  const fetchLeaderboard = useCallback(async () => {
    setLoadingMerchants(true)
    try {
      setMerchants(await getAdminAnalyticsMerchants(leaderboardDays))
    } finally {
      setLoadingMerchants(false)
    }
  }, [leaderboardDays])

  const fetchGaps = useCallback(async () => {
    setLoadingGaps(true)
    try {
      setGaps(await getAdminAnalyticsSearchGaps(days))
    } finally {
      setLoadingGaps(false)
    }
  }, [days])

  const fetchSources = useCallback(async () => {
    setLoadingSources(true)
    try {
      setSources(await getAdminAnalyticsSources(days))
    } finally {
      setLoadingSources(false)
    }
  }, [days])

  const fetchRetention = useCallback(async () => {
    setLoadingRetention(true)
    try {
      setRetention(await getAdminAnalyticsRetention(days))
    } finally {
      setLoadingRetention(false)
    }
  }, [days])

  const fetchChatContext = useCallback(async () => {
    setLoadingChatContext(true)
    try {
      // Raw-event backed → capped at the 90-day retention window.
      setChatContext(await getAdminAnalyticsChatContext(Math.min(days, 90)))
    } finally {
      setLoadingChatContext(false)
    }
  }, [days])

  useEffect(() => {
    fetchOverview()
    fetchGaps()
    fetchSources()
    fetchRetention()
    fetchChatContext()
  }, [fetchOverview, fetchGaps, fetchSources, fetchRetention, fetchChatContext])

  useEffect(() => {
    fetchLeaderboard()
  }, [fetchLeaderboard])

  useEffect(() => {
    getUserGrowth(Math.max(days, 30)).then(setGrowth).catch(() => {})
  }, [days])

  useEffect(() => {
    getTopUsers(days, 20, topMetric).then(setTopUsers).catch(() => {})
  }, [days, topMetric])

  const PRODUCT_COLOR = 'var(--tg-theme-button-color)'
  const MERCHANT_COLOR = '#f59e0b'

  return (
    <div className="p-4 space-y-6" style={{ paddingBottom: 80 }}>
      <style>{`@keyframes pulse{0%,100%{opacity:.4}50%{opacity:.7}}`}</style>

      {/* ── Global days picker ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ ...subtle, fontSize: 13 }}>Period:</span>
        <DaysPicker value={days} onChange={setDays} />
      </div>

      {/* ── 1. Platform Health ── */}
      <section>
        <div style={sectionTitle}>Platform Health</div>
        {loadingOverview ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} />)}
          </div>
        ) : overview ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 12 }}>
              <KpiCard label="Product Views" value={fmt(overview.total_product_views)} />
              <KpiCard label="Merchant Views" value={fmt(overview.total_merchant_views)} />
              <KpiCard
                label="Active Users"
                value={fmt(overview.active_users)}
                sub={`last ${days}d`}
              />
              <KpiCard
                label="Zero-Result Rate"
                value={`${overview.zero_result_search_rate}%`}
                sub="of all searches"
              />
            </div>

            {/* Platform breakdown */}
            <div style={{ ...card, marginBottom: 12 }}>
              <div style={{ ...subtle, fontSize: 12, marginBottom: 10 }}>Platform split</div>
              <BarChart
                items={[
                  { label: 'iOS', value: overview.platform_breakdown.ios },
                  { label: 'Android', value: overview.platform_breakdown.android },
                  { label: 'Web', value: overview.platform_breakdown.web },
                ]}
              />
            </div>

            {/* Daily views chart */}
            {overview.daily_series.length > 1 && (
              <div style={card}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 10,
                  }}
                >
                  <div style={{ ...subtle, fontSize: 12 }}>Daily views</div>
                  <div style={{ display: 'flex', gap: 12, fontSize: 12, ...subtle }}>
                    <span><Dot color={PRODUCT_COLOR} />Products</span>
                    <span><Dot color={MERCHANT_COLOR} />Merchants</span>
                  </div>
                </div>
                <LineChart
                  data={overview.daily_series as unknown as Record<string, number | string>[]}
                  keys={['product_views', 'merchant_views']}
                  labels={['Product views', 'Merchant views']}
                  colors={[PRODUCT_COLOR, MERCHANT_COLOR]}
                  height={170}
                />
              </div>
            )}
          </>
        ) : null}
      </section>

      {/* ── 2. Merchant Leaderboard ── */}
      <section>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <div style={sectionTitle}>Merchant Leaderboard</div>
          <DaysPicker value={leaderboardDays} onChange={setLeaderboardDays} />
        </div>
        {loadingMerchants ? (
          <Skeleton />
        ) : (
          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--app-page-bg)' }}>
                  {['#', 'Merchant', 'PV', 'MV', 'Likes', 'Conv%'].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: '10px 12px',
                        textAlign: h === '#' || h === 'Conv%' ? 'center' : 'left',
                        ...subtle,
                        fontWeight: 600,
                        fontSize: 11,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {merchants.map((m, idx) => (
                  <tr
                    key={m.merchant_id}
                    style={{
                      borderBottom:
                        idx < merchants.length - 1 ? '1px solid var(--app-page-bg)' : 'none',
                    }}
                  >
                    <td
                      style={{
                        padding: '10px 12px',
                        textAlign: 'center',
                        ...subtle,
                        fontWeight: 700,
                        fontSize: 12,
                      }}
                    >
                      {m.rank}
                    </td>
                    <td
                      style={{
                        padding: '10px 12px',
                        ...text,
                        fontWeight: 500,
                        maxWidth: 120,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {m.merchant_name}
                    </td>
                    <td style={{ padding: '10px 12px', ...text }}>{fmt(m.product_views)}</td>
                    <td style={{ padding: '10px 12px', ...text }}>{fmt(m.page_views)}</td>
                    <td style={{ padding: '10px 12px', ...text }}>{fmt(m.likes)}</td>
                    <td
                      style={{
                        padding: '10px 12px',
                        textAlign: 'center',
                        ...text,
                        fontWeight: 600,
                        color:
                          m.conversion_rate >= 5
                            ? '#22c55e'
                            : m.conversion_rate >= 2
                            ? '#f59e0b'
                            : 'var(--tg-theme-text-color)',
                      }}
                    >
                      {m.conversion_rate}%
                    </td>
                  </tr>
                ))}
                {merchants.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      style={{ padding: 24, textAlign: 'center', ...subtle, fontSize: 13 }}
                    >
                      No data for this period
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── 3. Catalog Gaps ── */}
      <section>
        <div style={sectionTitle}>Catalog Gaps</div>
        {loadingGaps ? (
          <Skeleton />
        ) : gaps ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 12 }}>
              <KpiCard
                label="Zero-result searches"
                value={fmt(gaps.zero_result_searches)}
                sub={`of ${fmt(gaps.total_searches)} total`}
              />
              <KpiCard
                label="Zero-result rate"
                value={`${gaps.zero_result_rate}%`}
                sub="catalog coverage"
              />
            </div>
            <div style={card}>
              <div style={{ ...subtle, fontSize: 12, marginBottom: 12 }}>
                Top queries with no results
              </div>
              {gaps.top_zero_result_queries.length === 0 ? (
                <div style={{ ...subtle, fontSize: 13, textAlign: 'center', padding: '8px 0' }}>
                  No zero-result queries — great coverage!
                </div>
              ) : (
                <BarChart
                  items={gaps.top_zero_result_queries.map((q) => ({
                    label: q.query,
                    value: q.count,
                  }))}
                  color="#ef4444"
                />
              )}
            </div>
          </>
        ) : null}
      </section>

      {/* ── 4. Traffic Sources ── */}
      <section>
        <div style={sectionTitle}>Traffic Sources</div>
        {loadingSources ? (
          <Skeleton />
        ) : sources ? (
          <>
            <div style={{ ...card, marginBottom: 12 }}>
              <div style={{ ...subtle, fontSize: 12, marginBottom: 12 }}>Source breakdown</div>
              <BarChart
                items={Object.entries(sources.totals)
                  .map(([k, v]) => ({ label: k.replace(/_/g, ' '), value: v }))
                  .sort((a, b) => b.value - a.value)}
              />
            </div>
            {sources.daily_trend.length > 1 && (
              <div style={card}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 10,
                  }}
                >
                  <div style={{ ...subtle, fontSize: 12 }}>Source trend</div>
                  <div style={{ display: 'flex', gap: 10, fontSize: 11, ...subtle }}>
                    <span><Dot color={PRODUCT_COLOR} />Direct</span>
                    <span><Dot color="#22c55e" />Search</span>
                    <span><Dot color={MERCHANT_COLOR} />Category</span>
                    <span><Dot color="#a855f7" />Shared</span>
                  </div>
                </div>
                <LineChart
                  data={sources.daily_trend as unknown as Record<string, number | string>[]}
                  keys={['direct', 'search', 'category', 'shared']}
                  labels={['Direct', 'Search', 'Category', 'Shared']}
                  colors={[PRODUCT_COLOR, '#22c55e', MERCHANT_COLOR, '#a855f7']}
                  height={170}
                />
              </div>
            )}
          </>
        ) : null}
      </section>

      {/* ── 5. Launch Context (Telegram chat_type / chat_instance) ── */}
      <section>
        <div style={sectionTitle}>
          Launch Context
          <InfoTooltip text="Where users open the Mini App from — based on Telegram's chat_type and chat_instance on initData." />
        </div>
        {loadingChatContext ? (
          <Skeleton />
        ) : chatContext ? (
          <>
            <div style={{ ...card, padding: 0, marginBottom: 12, overflow: 'hidden' }}>
              <div style={{ ...subtle, fontSize: 12, padding: '14px 14px 8px' }}>
                Breakdown by chat type (last {Math.min(days, 90)}d)
              </div>
              {chatContext.chat_type_breakdown.length === 0 ? (
                <div
                  style={{
                    ...subtle,
                    fontSize: 13,
                    textAlign: 'center',
                    padding: '12px 0 16px',
                  }}
                >
                  No launch-context data yet
                </div>
              ) : (
                (() => {
                  const sorted = [...chatContext.chat_type_breakdown].sort(
                    (a, b) =>
                      b.product_views + b.merchant_views - (a.product_views + a.merchant_views),
                  )
                  const maxTotal = Math.max(
                    1,
                    ...sorted.map((c) => c.product_views + c.merchant_views),
                  )
                  return (
                    <table
                      style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}
                    >
                      <thead>
                        <tr style={{ ...subtle, fontSize: 11 }}>
                          <th style={{ textAlign: 'left', padding: '6px 14px', fontWeight: 500 }}>
                            Type
                          </th>
                          <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 500 }}>
                            Products
                          </th>
                          <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 500 }}>
                            Merchants
                          </th>
                          <th
                            style={{ textAlign: 'right', padding: '6px 14px', fontWeight: 500 }}
                            title="Distinct chat_instance values seen for this chat_type"
                          >
                            Unique chats
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sorted.map((c, idx) => {
                          const total = c.product_views + c.merchant_views
                          const pct = (total / maxTotal) * 100
                          const color = CHAT_TYPE_COLOR[c.chat_type] ?? '#6b7280'
                          return (
                            <tr
                              key={c.chat_type}
                              style={{
                                borderTop:
                                  idx === 0 ? 'none' : '1px solid var(--app-page-bg)',
                              }}
                            >
                              <td style={{ padding: '8px 14px', position: 'relative' }}>
                                <div
                                  style={{
                                    position: 'absolute',
                                    left: 0,
                                    bottom: 0,
                                    height: 2,
                                    width: `${pct}%`,
                                    background: color,
                                    opacity: 0.7,
                                  }}
                                />
                                <ChatTypeBadge type={c.chat_type} />
                              </td>
                              <td
                                style={{
                                  padding: '8px',
                                  textAlign: 'right',
                                  ...text,
                                  fontWeight: 600,
                                }}
                              >
                                {fmt(c.product_views)}
                              </td>
                              <td
                                style={{
                                  padding: '8px',
                                  textAlign: 'right',
                                  ...text,
                                  fontWeight: 600,
                                }}
                              >
                                {fmt(c.merchant_views)}
                              </td>
                              <td
                                style={{
                                  padding: '8px 14px',
                                  textAlign: 'right',
                                  ...text,
                                  fontWeight: 600,
                                }}
                              >
                                {fmt(c.unique_instances)}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )
                })()
              )}
            </div>
            {chatContext.top_chat_instances.length > 0 && (
              <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
                <div
                  style={{
                    ...subtle,
                    fontSize: 12,
                    padding: '14px 14px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <span>
                    Top chat instances
                    <InfoTooltip text="chat_instance is an opaque per-chat identifier from Telegram — useful for spotting individual high-traffic chats." />
                  </span>
                </div>
                {/* Filter chips */}
                <div
                  style={{
                    display: 'flex',
                    gap: 6,
                    padding: '0 14px 10px',
                    flexWrap: 'wrap',
                  }}
                >
                  {(() => {
                    const presentTypes = Array.from(
                      new Set(chatContext.top_chat_instances.map((c) => c.chat_type)),
                    )
                    const chips: (string | null)[] = [null, ...presentTypes]
                    return chips.map((chip) => {
                      const active = instanceFilter === chip
                      const label = chip ?? 'All'
                      const color = chip ? CHAT_TYPE_COLOR[chip] ?? '#6b7280' : '#6b7280'
                      return (
                        <button
                          key={label}
                          type="button"
                          onClick={() => setInstanceFilter(chip)}
                          title={chip ? CHAT_TYPE_DESCRIPTIONS[chip] ?? '' : 'Show all chat types'}
                          style={{
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px 10px',
                            borderRadius: 999,
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: 'capitalize',
                            background: active ? color : `${color}22`,
                            color: active ? '#fff' : color,
                          }}
                        >
                          {label}
                        </button>
                      )
                    })
                  })()}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ ...subtle, fontSize: 11 }}>
                      <th style={{ textAlign: 'left', padding: '6px 14px', fontWeight: 500 }}>
                        chat_instance
                      </th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500 }}>
                        Type
                      </th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 500 }}>
                        Products
                      </th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 500 }}>
                        Merchants
                      </th>
                      <th style={{ textAlign: 'right', padding: '6px 14px', fontWeight: 500 }}>
                        Last seen
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const filtered = instanceFilter
                        ? chatContext.top_chat_instances.filter(
                            (c) => c.chat_type === instanceFilter,
                          )
                        : chatContext.top_chat_instances
                      if (filtered.length === 0) {
                        return (
                          <tr>
                            <td
                              colSpan={5}
                              style={{
                                ...subtle,
                                fontSize: 12,
                                textAlign: 'center',
                                padding: '14px 0',
                              }}
                            >
                              No instances of this type
                            </td>
                          </tr>
                        )
                      }
                      return filtered.map((c, idx) => (
                        <tr
                          key={c.chat_instance}
                          style={{
                            borderTop:
                              idx === 0 ? '1px solid var(--app-page-bg)' : '1px solid var(--app-page-bg)',
                          }}
                        >
                          <td
                            title={c.chat_instance}
                            style={{
                              padding: '8px 14px',
                              ...text,
                              fontFamily: 'monospace',
                              maxWidth: 160,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {c.chat_instance}
                          </td>
                          <td style={{ padding: '8px' }}>
                            <ChatTypeBadge type={c.chat_type} />
                          </td>
                          <td
                            style={{
                              padding: '8px',
                              textAlign: 'right',
                              ...text,
                              fontWeight: 600,
                            }}
                          >
                            {fmt(c.product_views)}
                          </td>
                          <td
                            style={{
                              padding: '8px',
                              textAlign: 'right',
                              ...text,
                              fontWeight: 600,
                            }}
                          >
                            {fmt(c.merchant_views)}
                          </td>
                          <td
                            style={{
                              padding: '8px 14px',
                              textAlign: 'right',
                              ...subtle,
                              whiteSpace: 'nowrap',
                            }}
                            title={c.last_seen_at ?? ''}
                          >
                            {formatLastSeen(c.last_seen_at)}
                          </td>
                        </tr>
                      ))
                    })()}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : null}
      </section>

      {/* ── 6. Retention ── */}
      <section>
        <div style={sectionTitle}>User Retention</div>
        {loadingRetention ? (
          <Skeleton />
        ) : retention ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 12 }}>
              <KpiCard
                label="Unique users"
                value={fmt(retention.total_unique_users)}
                sub={`last ${days}d`}
              />
              <KpiCard
                label="Repeat visitor rate"
                value={`${retention.repeat_visitor_rate}%`}
                sub={`${fmt(retention.repeat_visitor_count)} users`}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12, alignItems: 'start' }}>
            {/* New vs Returning weekly */}
            {retention.new_vs_returning_weekly.length > 0 && (
              <div style={card}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 10,
                  }}
                >
                  <div style={{ ...subtle, fontSize: 12 }}>New vs Returning (weekly)</div>
                  <div style={{ display: 'flex', gap: 10, fontSize: 11, ...subtle }}>
                    <span><Dot color={PRODUCT_COLOR} />New</span>
                    <span><Dot color={MERCHANT_COLOR} />Returning</span>
                  </div>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      {['Week', 'Total', 'New', 'Returning'].map((h) => (
                        <th
                          key={h}
                          style={{
                            padding: '6px 8px',
                            textAlign: h === 'Week' ? 'left' : 'right',
                            ...subtle,
                            fontWeight: 600,
                            fontSize: 11,
                            textTransform: 'uppercase',
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {retention.new_vs_returning_weekly.map((w) => (
                      <tr key={w.week}>
                        <td style={{ padding: '6px 8px', ...subtle }}>{w.week.slice(0, 10)}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', ...text }}>
                          {fmt(w.total_active)}
                        </td>
                        <td
                          style={{
                            padding: '6px 8px',
                            textAlign: 'right',
                            color: PRODUCT_COLOR,
                            fontWeight: 600,
                          }}
                        >
                          {fmt(w.new_users)}
                        </td>
                        <td
                          style={{
                            padding: '6px 8px',
                            textAlign: 'right',
                            color: MERCHANT_COLOR,
                            fontWeight: 600,
                          }}
                        >
                          {fmt(w.returning_users)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Language breakdown */}
            {retention.language_breakdown.length > 0 && (
              <div style={card}>
                <div style={{ ...subtle, fontSize: 12, marginBottom: 12 }}>
                  Language segments
                </div>
                <BarChart
                  items={retention.language_breakdown.map((l) => ({
                    label: l.language.toUpperCase(),
                    value: l.users,
                  }))}
                  color="#22c55e"
                />
              </div>
            )}
            </div>
          </>
        ) : null}
      </section>

      {/* ── User Growth ─────────────────────────────────────────────── */}
      <section>
        <div style={sectionTitle}>User Growth</div>
        {growth && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8, marginBottom: 12 }}>
              <KpiCard
                label="Total platform users"
                value={fmt(growth.total_users)}
                sub="merchant-side accounts"
              />
              <KpiCard
                label="Total browsers"
                value={fmt(growth.total_public_users)}
                sub="public-side accounts"
              />
            </div>
            {growth.daily.length > 0 && (
              <div style={card}>
                <div style={{ ...subtle, fontSize: 12, marginBottom: 10 }}>
                  Daily new signups (last {growth.period_days}d)
                </div>
                <LineChart
                  data={growth.daily.map((d) => ({
                    date: d.date,
                    new_users: d.new_users,
                    new_public_users: d.new_public_users,
                  }))}
                  keys={['new_public_users', 'new_users']}
                  labels={['Browsers', 'Merchants']}
                  colors={['#22c55e', 'var(--tg-theme-button-color)']}
                  height={170}
                />
              </div>
            )}
          </>
        )}
      </section>

      {/* ── Top Users ──────────────────────────────────────────────── */}
      <section>
        <div style={sectionTitle}>Top Users</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          {(['views', 'sessions', 'likes'] as const).map((m) => (
            <button
              key={m}
              onClick={() => setTopMetric(m)}
              style={{
                padding: '6px 12px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 500,
                border: 'none',
                cursor: 'pointer',
                background: topMetric === m ? 'var(--tg-theme-button-color)' : 'var(--app-card-bg)',
                color: topMetric === m ? 'var(--tg-theme-button-text-color)' : 'var(--tg-theme-text-color)',
              }}
            >
              {m}
            </button>
          ))}
        </div>
        <div style={card}>
          {topUsers.length === 0 ? (
            <div style={{ ...subtle, fontSize: 12 }}>No data for this window.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {topUsers.map((u) => (
                <button
                  key={u.id}
                  onClick={() => navigate(`/admin/users/${u.id}`)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 10px',
                    borderRadius: 10,
                    border: 'none',
                    background: 'var(--app-page-bg)',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <span style={{ ...subtle, fontSize: 11, width: 22, textAlign: 'right' }}>
                    #{u.rank}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ ...text, fontSize: 13, fontWeight: 500 }}>
                      {[u.first_name, u.last_name].filter(Boolean).join(' ') ||
                        u.telegram_username ||
                        `#${u.telegram_id}`}
                    </div>
                    <div style={{ ...subtle, fontSize: 11, fontFamily: 'monospace' }}>
                      {u.telegram_username ? `@${u.telegram_username} · ` : ''}
                      {u.telegram_id}
                    </div>
                  </div>
                  <span style={{ ...text, fontSize: 13, fontWeight: 700 }}>{fmt(u.score)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
